import React, { useState, useEffect, useCallback } from 'react';
import { featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { 
  AlertTriangle, 
  Shield, 
  CheckCircle, 
  Clock, 
  Info,
  ChevronDown,
  ChevronUp,
  History,
  RefreshCw
} from 'lucide-react';

const FeatureTogglesPage = () => {
  const [flags, setFlags] = useState([]);
  const [groupedFlags, setGroupedFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({
    phase_6: true,
    phase_5: false,
    phase_4: false,
    phase_3: false,
    phase_2: false
  });
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [filter, setFilter] = useState('all'); // all | enabled | disabled | high_risk
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmReason, setConfirmReason] = useState('');
  const [pendingToggle, setPendingToggle] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchFlags = useCallback(async () => {
    try {
      setLoading(true);
      const data = await featureFlagsService.getAllFlags();
      setFlags(data.flags);
      setGroupedFlags(data.grouped);
      setCanEdit(data.can_edit === true);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch feature flags:', err);
      setError('Failed to load feature flags');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlags();
  }, [fetchFlags]);

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const getRiskBadge = (riskLevel) => {
    const config = {
      high: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, label: 'HIGH RISK' },
      medium: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Shield, label: 'MEDIUM' },
      low: { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle, label: 'LOW' }
    };
    const { color, icon: Icon, label } = config[riskLevel] || config.low;
    return (
      <Badge className={`${color} border flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  const getPhaseLabel = (phase) => {
    const labels = {
      phase_6: 'Phase 6 - Future Modules (Disabled)',
      phase_5: 'Phase 5 - Staff & Operations',
      phase_4: 'Phase 4 - Advanced Operations',
      phase_3: 'Phase 3 - External Integrations',
      phase_2: 'Phase 2 - Core Features',
      quote_lead: 'Quote & Lead',
      neo_vnext: 'Enterprise Intelligence (vNext)',
      expansion: 'Expansion',
    };
    return labels[phase] || phase;
  };

  const getPhaseDescription = (phase) => {
    const descriptions = {
      phase_6: 'Dispatch Automation, Load Boards, Payroll Integration, Advanced Analytics, Driver Dashboard',
      phase_5: 'Staff Roles, Maintenance Module, Accounting & Payment Processing',
      phase_4: 'Dispatch, Load Management, GPS/ELD Tracking, Analytics & Broker Portal',
      phase_3: 'Notifications, Digital Signatures, Compliance Integrations',
      phase_2: 'Driver Lifecycle, Settlements, Background Tasks',
      quote_lead: 'Quote Builder, Lead Capture, Quote Workflow, Reporting & Notifications',
      neo_vnext: 'Relationship Engine, Universal Search & enterprise intelligence capabilities',
      expansion: 'Partner Portal & expansion capabilities',
    };
    return descriptions[phase] || '';
  };

  // Order phases sensibly; append any unknown phases at the end.
  const PHASE_ORDER = ['quote_lead', 'neo_vnext', 'expansion', 'phase_2', 'phase_3', 'phase_4', 'phase_5', 'phase_6'];
  const matchesFilter = (f) => (
    filter === 'all'
    || (filter === 'enabled' && f.enabled)
    || (filter === 'disabled' && !f.enabled)
    || (filter === 'high_risk' && f.enabled && f.risk_level === 'high')
  );

  const handleToggleClick = (flag, newValue) => {
    if (flag.risk_level === 'high' || flag.requires_confirmation) {
      setPendingToggle({ flag, newValue });
      setShowConfirmModal(true);
      setConfirmReason('');
    } else {
      performToggle(flag.key, newValue, null);
    }
  };

  const performToggle = async (flagKey, enabled, reason) => {
    try {
      setToggling(true);
      await featureFlagsService.toggleFlag(flagKey, enabled, reason);
      await fetchFlags();
      setShowConfirmModal(false);
      setPendingToggle(null);
      setConfirmReason('');
    } catch (err) {
      console.error('Failed to toggle flag:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to toggle feature';
      alert(errorMsg);
    } finally {
      setToggling(false);
    }
  };

  const handleConfirmToggle = () => {
    if (pendingToggle) {
      const { flag, newValue } = pendingToggle;
      if (flag.requires_reason && newValue && !confirmReason.trim()) {
        alert('A reason is required for enabling this HIGH-risk feature');
        return;
      }
      performToggle(flag.key, newValue, confirmReason || null);
    }
  };

  const viewHistory = async (flag) => {
    try {
      setHistoryLoading(true);
      setSelectedFlag(flag);
      setShowHistoryModal(true);
      const data = await featureFlagsService.getFlagHistory(flag.key, 20);
      setAuditHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
      setAuditHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const enabledCount = flags.filter(f => f.enabled).length;
  const highRiskEnabled = flags.filter(f => f.enabled && f.risk_level === 'high').length;
  const disabledCount = flags.length - enabledCount;

  // Group client-side from the full flag list so EVERY phase is represented
  // (the registry has phases beyond the legacy phase_2..phase_6 set).
  const groupsByPhase = flags.reduce((acc, f) => {
    const p = f.phase || 'phase_4';
    (acc[p] = acc[p] || []).push(f);
    return acc;
  }, {});
  const presentPhases = Object.keys(groupsByPhase).sort((a, b) => {
    const ia = PHASE_ORDER.indexOf(a); const ib = PHASE_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  const filtering = filter !== 'all';
  const cardCls = (active) => `text-left w-full transition-all ${active ? 'ring-2 ring-blue-500' : 'hover:shadow-md'}`;
  const setFilterToggle = (v) => setFilter((cur) => (cur === v ? 'all' : v));

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Feature Toggles</h1>
          <p className="text-gray-600 mt-1">Manage system features and Phase 4 rollout</p>
        </div>

        {/* Summary Cards — clickable filters that reveal the exact underlying records */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
          <button type="button" onClick={() => setFilter('all')} data-testid="ft-card-total" className={cardCls(filter === 'all')}>
            <Card className={filter === 'all' ? 'border-blue-300' : ''}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-gray-900">{flags.length}</div>
                <div className="text-sm text-gray-500">Total Features</div>
              </CardContent>
            </Card>
          </button>
          <button type="button" onClick={() => setFilterToggle('enabled')} data-testid="ft-card-enabled" className={cardCls(filter === 'enabled')}>
            <Card className={filter === 'enabled' ? 'border-green-300 bg-green-50' : ''}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">{enabledCount}</div>
                <div className="text-sm text-gray-500">Enabled</div>
              </CardContent>
            </Card>
          </button>
          <button type="button" onClick={() => setFilterToggle('disabled')} data-testid="ft-card-disabled" className={cardCls(filter === 'disabled')}>
            <Card className={filter === 'disabled' ? 'border-gray-400 bg-gray-50' : ''}>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-gray-400">{disabledCount}</div>
                <div className="text-sm text-gray-500">Disabled</div>
              </CardContent>
            </Card>
          </button>
          <button type="button" onClick={() => setFilterToggle('high_risk')} data-testid="ft-card-highrisk" className={cardCls(filter === 'high_risk')}>
            <Card className={filter === 'high_risk' || highRiskEnabled > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="pt-4">
                <div className={`text-2xl font-bold ${highRiskEnabled > 0 ? 'text-red-600' : 'text-gray-400'}`}>{highRiskEnabled}</div>
                <div className="text-sm text-gray-500">High Risk Active</div>
              </CardContent>
            </Card>
          </button>
        </div>
        {filtering && (
          <div className="mb-6 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2" data-testid="ft-filter-banner">
            <span className="text-sm text-blue-800">
              Showing <strong>{flags.filter(matchesFilter).length}</strong> {filter === 'high_risk' ? 'high-risk active' : filter} feature(s).
            </span>
            <button onClick={() => setFilter('all')} data-testid="ft-clear-filter" className="text-sm font-medium text-blue-700 hover:text-blue-900">Clear filter</button>
          </div>
        )}
        {!filtering && <div className="mb-4" />}

        {/* Read-Only Banner for Partners */}
        {!canEdit && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-blue-800">View Only Mode</div>
              <div className="text-sm text-blue-700">
                You have read-only access to feature flags. Contact a Super Admin to make changes.
              </div>
            </div>
          </div>
        )}

        {/* Warning Banner */}
        {highRiskEnabled > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-800">High-Risk Features Active</div>
              <div className="text-sm text-red-700">
                {highRiskEnabled} high-risk feature(s) are currently enabled. Monitor system closely.
              </div>
            </div>
          </div>
        )}

        {/* Phase 6 Warning Banner - Always show */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-amber-800">Phase 6 - Future Modules</div>
            <div className="text-sm text-amber-700">
              <strong>Do not enable without approval.</strong> Phase 6 features are scaffolded placeholders for future development. 
              Enabling them will only show preview interfaces - no actual functionality is connected.
            </div>
          </div>
        </div>

        {/* Foundation (Phase 1) — visible, read-only. Not represented as toggleable flags. */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="ft-foundation-note">
          <Shield className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-slate-800">Phase 1 — Foundation (Locked / Read-Only)</div>
            <div className="text-sm text-slate-600">
              Foundational backbone capabilities (authentication, RBAC, audit, core data model) are <strong>not exposed as toggleable feature flags</strong> and cannot be switched off from this screen by design.
              Toggling here is restricted server-side to <strong>Super Admin</strong>, and high-risk changes require explicit confirmation and a reason. No client-side unlock exists — foundation changes are made through governed code/deploy, not casual toggles.
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        {/* Feature Groups — every phase in the registry, honoring the active filter */}
        <div className="space-y-4">
          {presentPhases.map(phase => {
            const all = groupsByPhase[phase] || [];
            const shown = all.filter(matchesFilter);
            if (filtering && shown.length === 0) return null;
            const isOpen = filtering ? true : expandedGroups[phase];
            return (
            <Card key={phase} className="overflow-hidden" data-testid={`ft-phase-${phase}`}>
              <CardHeader 
                className="cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => !filtering && toggleGroup(phase)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">{getPhaseLabel(phase)}</CardTitle>
                    <CardDescription>{getPhaseDescription(phase)}</CardDescription>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">
                      {all.filter(f => f.enabled).length} / {all.length} enabled
                    </Badge>
                    {!filtering && (isOpen ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ))}
                  </div>
                </div>
              </CardHeader>
              
              {isOpen && (
                <CardContent className="pt-0">
                  <div className="divide-y">
                    {shown.map(flag => (
                      <div key={flag.key} className="py-4 first:pt-0 last:pb-0">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900">{flag.name}</span>
                              {getRiskBadge(flag.risk_level)}
                              {flag.requires_confirmation && (
                                <Badge variant="outline" className="text-xs">
                                  Requires Confirmation
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{flag.description}</p>
                            {flag.dependencies?.length > 0 && (
                              <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                                <Info className="w-3 h-3" />
                                Depends on: {flag.dependencies.join(', ')}
                              </div>
                            )}
                            {flag.updated_at && (
                              <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                                <Clock className="w-3 h-3" />
                                Last updated: {formatDate(flag.updated_at)} by {flag.updated_by || 'system'}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewHistory(flag);
                              }}
                            >
                              <History className="w-4 h-4" />
                            </Button>
                            <div className="flex items-center gap-2">
                              <span className={`text-sm font-medium ${flag.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                                {flag.enabled ? 'ON' : 'OFF'}
                              </span>
                              {canEdit ? (
                                <Switch
                                  checked={flag.enabled}
                                  onCheckedChange={(checked) => handleToggleClick(flag, checked)}
                                  disabled={toggling}
                                />
                              ) : (
                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">View Only</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
            );
          })}
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && pendingToggle && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className={`w-6 h-6 flex-shrink-0 ${pendingToggle.flag.risk_level === 'high' ? 'text-red-600' : 'text-yellow-600'}`} />
                <div>
                  <h3 className="font-semibold text-lg">
                    {pendingToggle.newValue ? 'Enable' : 'Disable'} Feature
                  </h3>
                  <p className="text-gray-600 text-sm mt-1">
                    You are about to {pendingToggle.newValue ? 'enable' : 'disable'} <strong>{pendingToggle.flag.name}</strong>
                  </p>
                </div>
              </div>

              {pendingToggle.flag.risk_level === 'high' && (
                <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                  <div className="text-sm text-red-800">
                    <strong>Warning:</strong> This is a HIGH-RISK feature. Enabling it may affect system operations.
                  </div>
                </div>
              )}

              {pendingToggle.flag.requires_reason && pendingToggle.newValue && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for enabling (required)
                  </label>
                  <textarea
                    className="w-full border rounded-md p-2 text-sm"
                    rows={3}
                    value={confirmReason}
                    onChange={(e) => setConfirmReason(e.target.value)}
                    placeholder="Enter the reason for enabling this feature..."
                  />
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConfirmModal(false);
                    setPendingToggle(null);
                    setConfirmReason('');
                  }}
                  disabled={toggling}
                >
                  Cancel
                </Button>
                <Button
                  variant={pendingToggle.flag.risk_level === 'high' ? 'destructive' : 'default'}
                  onClick={handleConfirmToggle}
                  disabled={toggling}
                >
                  {toggling ? 'Processing...' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* History Modal */}
        {showHistoryModal && selectedFlag && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] flex flex-col">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-lg">Audit History</h3>
                <p className="text-sm text-gray-600">{selectedFlag.name}</p>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                ) : auditHistory.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    No history available
                  </div>
                ) : (
                  <div className="space-y-3">
                    {auditHistory.map((entry, idx) => (
                      <div key={idx} className="border rounded p-3 text-sm">
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={entry.new_value ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {entry.new_value ? 'ENABLED' : 'DISABLED'}
                          </Badge>
                          <span className="text-gray-400 text-xs">
                            {formatDate(entry.timestamp)}
                          </span>
                        </div>
                        <div className="text-gray-600">
                          Changed by: {entry.admin_email || 'Unknown'}
                        </div>
                        {entry.reason && (
                          <div className="text-gray-500 mt-1 italic">
                            Reason: {entry.reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowHistoryModal(false);
                    setSelectedFlag(null);
                    setAuditHistory([]);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FeatureTogglesPage;
